// eslint-disable-next-line
import { action,runInAction, makeAutoObservable } from "mobx";
import { rootContainer, TYPE } from "../../../services/container/root";
import { ISaveable } from "../../../stores/types";
import LocalesService, { ILocaleItem, LocaleStatus } from "../../../services/api/locales-service";
import LocaleStore from "./locale-store";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IFileData } from "../../../components/file-upload/file-upload";
import {
    trackLanguageConfigurationCompleted,
    trackTranslationsCsvDownloadCompleted,
    trackTranslationsCsvUploadCompleted,
} from "../../../translation/metrics/translations-mixpanel";

export type TranslationsCsvSourcePage = "languages_hub" | "add_language_wizard";

export class LocalesListStore implements ISaveable{

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }
    
    private _isLoading: boolean = false;
    private _localesList: LocaleStore[] = [];
    private _isSaving: boolean = false;
    private _isDeleting: boolean = false;
    private _isAdding: boolean = false;
    private _selectLocalesList: IOption[] = [] as IOption[];
    private _isDownloading: boolean = false;
    private _isUploading: boolean = false;
    private _uploadedSuccessfuly: boolean = false;
    private _isAddLanguageWizardOpen: boolean = false;

    
    protected get service(): LocalesService { return rootContainer.get<LocalesService>(TYPE.LocaleService); }
    public get isAddLanguageWizardOpen(): boolean { return this._isAddLanguageWizardOpen; }
    public get localesList(): LocaleStore[] { return this._localesList };
    public get selectLocalesList(): IOption[] { 
        const filteredList  = this._selectLocalesList.filter(item => {
            return !this.localesList.some(l => l.localeCode === item.value);
        });
        return filteredList.sort((a, b) => (a.label > b.label) ?1 :-1);
    };

    // Apparently, selectLocalesList takes word "select" from UI pattern, as it's used after all in <select>
    // makes more sense to me to not tightly couple method name to UI elements
    // it just contains all the available locales in an unfiltered list
    // During CR, acknowledged that we're aware of the naming issue in this store.
    // To be handled separately in https://returngo.atlassian.net/browse/ET-1051
    public get getAllLocales(): IOption[] {
        return this._selectLocalesList;
    }
    
    public isLoading(): boolean {return this._isLoading};
    public isSaving(): boolean {return this._isSaving};
    public isDeleting(): boolean { return this._isDeleting};
    public isDownloading(): boolean { return this._isDownloading};
    public isUploading(): boolean { return this._isUploading};
    public isBusy(): boolean { return this._isLoading || this._isSaving || this._isDeleting || this._isAdding };
    public hasChanges(): boolean { return this._localesList.some(locale => locale.hasChanges()) };
    public uploadedSuccessfuly(): boolean {return this._uploadedSuccessfuly }

    @action.bound
    async load() {
        this._isLoading = true;
        // Await labels first so Supported languages can sort A–Z by name.
        await this.getSelectLocalesList();
		const localesData = await this.service.fetchShopLocales();
		runInAction(() => {
			this._setLocalesList(localesData);
            this._isLoading = false;
		});
	}

    public async save(): Promise<void> {
       this._isSaving = true;
       const localesWithChanges = this.localesList.filter(locale => locale.hasChanges());
       let localesToSave = [] as ILocaleItem[];
       this.localesList.forEach(locale => {
           localesToSave.push(locale.getUpdatedModel());
       });
       const __old = this.localesList.map(l => l.model);
       try {
           const savedLocalesList = await this.service.handleSave({ payload: localesToSave, __old });
           runInAction(() => {
               this._setLocalesList(savedLocalesList);
               this._isSaving = false;
           });
           const enabledCount = this.enabledLocaleCount();
           localesWithChanges.forEach(locale => {
               const previous = locale.model;
               const next = locale.getUpdatedModel();
               if (previous.is_enabled !== next.is_enabled) {
                   trackLanguageConfigurationCompleted({
                       action: next.is_enabled ? "enable" : "disable",
                       locale: locale.localeCode,
                       result: "success",
                       enabled_locale_count: enabledCount,
                   });
               }
               if (!previous.is_default && next.is_default) {
                   trackLanguageConfigurationCompleted({
                       action: "set_default",
                       locale: locale.localeCode,
                       result: "success",
                       enabled_locale_count: enabledCount,
                   });
               }
           });
       } catch {
           runInAction(() => {
               this._isSaving = false;
           });
           localesWithChanges.forEach(locale => {
               const previous = locale.model;
               const next = locale.getUpdatedModel();
               const actionType = !previous.is_default && next.is_default
                   ? "set_default"
                   : next.is_enabled
                       ? "enable"
                       : "disable";
               trackLanguageConfigurationCompleted({
                   action: actionType,
                   locale: locale.localeCode,
                   result: "error",
                   enabled_locale_count: this.enabledLocaleCount(),
               });
           });
       }
    }

    public async handleAddLocales(locales: string[]): Promise<boolean> {
        let isSucceeded = false;
        try {
            this._isAdding = true;
            const newLocalesList = await this.service.addLocales(locales);
            runInAction(() => {
                this._setLocalesList(newLocalesList);
                this._isAdding = false;
            });
            isSucceeded = true;
            const enabledCount = this.enabledLocaleCount();
            locales.forEach(locale => {
                trackLanguageConfigurationCompleted({
                    action: "add",
                    locale,
                    result: "success",
                    enabled_locale_count: enabledCount,
                });
            });
        }
        catch (e) {
            runInAction(() => {
                this._isAdding = false;
            });
            locales.forEach(locale => {
                trackLanguageConfigurationCompleted({
                    action: "add",
                    locale,
                    result: "error",
                    enabled_locale_count: this.enabledLocaleCount(),
                });
            });
        }
        return isSucceeded;
    }

    public async downloadTranslations(sourcePage: TranslationsCsvSourcePage = "languages_hub"): Promise<void> {
        try{
            this._isDownloading = true;
            const path = await this.service.downloadTranslations();
            window.open(path);
            trackTranslationsCsvDownloadCompleted({
                enabled_locale_count: this.enabledLocaleCount(),
                source_page: sourcePage,
            });
            runInAction(() => {
                this._isDownloading = false;
            });
        } catch {
            runInAction(() => {
                this._isDownloading = false;
            });
        }

    }

    public async uploadTranslationsFile(file: File, fileContent: string | ArrayBuffer | null | undefined): Promise<boolean> {
        let isSuccesded = false;
        const startedAt = Date.now();
        const missingBefore = this.countLocalesWithMissingTranslations();
        const localeCountBefore = this._localesList.length;
        try {
            if (fileContent) {
                this._uploadedSuccessfuly = false;
                this._isUploading = true;
                const data: IFileData = {
                    file_data: fileContent,
                    mime_type: file.type,
                    original_name: file.name,
                    size: file.size,
                }
                const updatedLocales: ILocaleItem[] = await this.service.uploadTranslationsFile(data);

                if(!updatedLocales) {
                    this._isUploading = false;
                    throw new Error('Error: did not receive updated locales');
                }

                runInAction(() => {
                    /** Detect new locales */
                    updatedLocales.forEach((newLocale) => {
                        if (!this._localesList.some((locale) => locale.localeCode === newLocale.locale)) {
                            sessionStorage.setItem(newLocale.locale, "new");
                        }
                    })
                    /** Update locales list */
                    this._setLocalesList(updatedLocales);
                    this._isUploading = false;
                    this._uploadedSuccessfuly = true;
                })
                isSuccesded = true;
                trackTranslationsCsvUploadCompleted({
                    result: "success",
                    locale_count: updatedLocales.length,
                    changed_rows_count: Math.max(updatedLocales.length - localeCountBefore, 0),
                    missing_before: missingBefore,
                    missing_after: this.countLocalesWithMissingTranslations(),
                    duration_ms: Date.now() - startedAt,
                });
            } else {
                this._isUploading = false;
                throw new Error('no files to upload');
            }
        } catch (e) {
            runInAction(() => {
                this._isUploading = false;
            });
            isSuccesded = false;
            trackTranslationsCsvUploadCompleted({
                result: "error",
                locale_count: this._localesList.length,
                changed_rows_count: 0,
                missing_before: missingBefore,
                missing_after: missingBefore,
                duration_ms: Date.now() - startedAt,
            });
        }
        return isSuccesded;
    }

    private async getSelectLocalesList(): Promise<void> {
        const selectedLocalesList = await this.service.getSelectLocalesList();
        runInAction(() => {
            this._selectLocalesList = selectedLocalesList;
        });
    }

    private onDefaultLocaleChange(locale: string): void {
        this._localesList.forEach(store => {
            if (store.localeCode !== locale) {
                store.unsetDefault();
            }
        });
        // Keep default first as soon as the merchant picks a new one.
        this._localesList = this._sortLocales(this._localesList);
    }

    private async onDelete(localeToDelete: string): Promise<void> {
        this._isDeleting = true;
        const __old = this.localesList.map(l => l.model);
        try {
            const updatedLocalesList = await this.service.handleDelete({ payload: { locale: localeToDelete }, __old })
            runInAction(() => {
                this._setLocalesList(updatedLocalesList);
                this._isDeleting = false;
            });
            trackLanguageConfigurationCompleted({
                action: "delete",
                locale: localeToDelete,
                result: "success",
                enabled_locale_count: this.enabledLocaleCount(),
            });
        } catch {
            runInAction(() => {
                this._isDeleting = false;
            });
            trackLanguageConfigurationCompleted({
                action: "delete",
                locale: localeToDelete,
                result: "error",
                enabled_locale_count: this.enabledLocaleCount(),
            });
        }
    }

    private getLocaleName(code: string): string {
        const localeName = this._selectLocalesList && this._selectLocalesList.find(locale => locale.value === code)?.label;
        return localeName ?? code;
    }

    private _setLocalesList(localesData: ILocaleItem[]): void {
        const stores = localesData.map(model => {
            return new LocaleStore(model, this.onDefaultLocaleChange, this.onDelete, this.getLocaleName);
        });
        this._localesList = this._sortLocales(stores);
    }

    /**
     * Default language first; remaining rows A–Z by display name.
     */
    private _sortLocales(locales: LocaleStore[]): LocaleStore[] {
        return [...locales].sort((a, b) => {
            if (a.is_default !== b.is_default) {
                return a.is_default ? -1 : 1;
            }
            return a.localeName.localeCompare(b.localeName, undefined, { sensitivity: 'base' });
        });
    }

    public enabledLocaleCount(): number {
        return this._localesList.filter(locale => locale.enabled).length;
    }

    public countLocalesWithMissingTranslations(): number {
        return this._localesList.filter(locale =>
            locale.localeStatus === LocaleStatus.MISSING_TRANSLATIONS
            || locale.localeStatus === LocaleStatus.NO_TRANSLATIONS
        ).length;
    }


    public setIsAddLanguageWizardOpen(v: boolean): void {
        this._isAddLanguageWizardOpen = v;
    }

}

export default new LocalesListStore();